import { useMemo, useEffect } from "react";
import { Card, Badge, Stack, ProgressBar } from "react-bootstrap";
import { useUnclaimedRecords } from "./hooks/useUnclaimedRecords";
import { Currency } from "../../../lib/components/Currency";
import { CancelButton } from "../shared";

const AGE_BUCKETS = [
  {
    key: "0-30",
    label: "0\u201330 days",
    max: 30,
    variant: "success",
    text: "text-secondary",
  },
  {
    key: "31-60",
    label: "31\u201360 days",
    max: 60,
    variant: "info",
    text: "text-secondary",
  },
  {
    key: "61-90",
    label: "61\u201390 days",
    max: 90,
    variant: "warning",
    text: "text-warning-emphasis",
  },
  {
    key: "90+",
    label: "90+ days",
    max: Infinity,
    variant: "danger",
    text: "text-danger-emphasis",
  },
];

function getAgeBucket(dateStr) {
  if (!dateStr) return AGE_BUCKETS[AGE_BUCKETS.length - 1];
  const days = (Date.now() - new Date(dateStr).valueOf()) / 86400000;
  return (
    AGE_BUCKETS.find((b) => days <= b.max) ??
    AGE_BUCKETS[AGE_BUCKETS.length - 1]
  );
}

export function UnclaimedPool({
  api,
  onDrillDown,
  customerCode,
  refreshToken,
  onLoadingChange,
  onError,
}) {
  const { pool, isLoading, hasLoaded } = useUnclaimedRecords({
    api,
    customerCode,
    refreshToken,
    onError,
  });

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  const stats = useMemo(() => {
    const byType = { commission: 0, bonus: 0, chargeback: 0 };
    let value = 0;
    const bucketMap = Object.fromEntries(
      AGE_BUCKETS.map((b) => [b.key, { ...b, count: 0, value: 0 }]),
    );

    pool.forEach((r) => {
      value += r.amount;
      byType[r.type] += 1;

      const bucket = getAgeBucket(r.created);
      bucketMap[bucket.key].count += 1;
      bucketMap[bucket.key].value += r.amount;
    });

    const buckets = AGE_BUCKETS.map((b) => bucketMap[b.key]).filter(
      (b) => b.count > 0,
    );

    return { byType, value, buckets };
  }, [pool]);

  if (!hasLoaded) return null;

  return (
    <Card className="run-overview-card mb-4">
      <div className="run-overview-header">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 w-100">
          <div>
            <div className="tnum fs-2">{pool.length.toLocaleString()}</div>
            <div className="text-secondary small">
              unclaimed records &middot; <Currency amount={stats.value} />
            </div>
          </div>
          {onDrillDown && (
            <CancelButton text="View records &#8599;" onClick={onDrillDown} />
          )}
        </div>
      </div>

      {pool.length > 0 && (
        <Card.Body className="run-overview-body">
          <>
            {stats.buckets.length > 0 && (
              <>
                <ProgressBar className="mt-4" style={{ height: 8 }}>
                  {stats.buckets.map((b) => (
                    <ProgressBar
                      key={b.key}
                      variant={b.variant}
                      now={(b.count / pool.length) * 100}
                      aria-label={`${b.label}: ${b.count} records`}
                    />
                  ))}
                </ProgressBar>

                <Stack
                  direction="horizontal"
                  gap={4}
                  className="mt-3 flex-wrap"
                >
                  {stats.buckets.map((b) => (
                    <span
                      key={b.key}
                      className="d-flex align-items-center gap-2"
                    >
                      <span
                        className={`d-inline-block rounded-circle bg-${b.variant}`}
                        style={{ width: 8, height: 8 }}
                      />
                      <span className={`small ${b.text}`}>{b.label}</span>
                      <span className="small tnum">
                        {b.count.toLocaleString()}
                      </span>
                      <span className="small tnum text-secondary">
                        <Currency amount={b.value} />
                      </span>
                    </span>
                  ))}
                </Stack>
              </>
            )}
            <Stack
              direction="horizontal"
              gap={2}
              className="mt-4 pt-3 border-top flex-wrap"
            >
              <Badge bg="secondary" className="fw-normal">
                {stats.byType.commission.toLocaleString()} commissions
              </Badge>
              <Badge bg="secondary" className="fw-normal">
                {stats.byType.bonus.toLocaleString()} bonuses
              </Badge>
              <Badge bg="secondary" className="fw-normal">
                {stats.byType.chargeback.toLocaleString()} chargebacks
              </Badge>
            </Stack>
          </>
        </Card.Body>
      )}
    </Card>
  );
}
